import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { WebhooksLogo, Plus, Trash, Eye, Lightning, CheckCircle, XCircle, Clock, Globe, ArrowClockwise } from "@phosphor-icons/react";

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ url: "", name: "", events: [] });
  const [logs, setLogs] = useState(null);
  const [logsWebhookId, setLogsWebhookId] = useState(null);

  const fetchWebhooks = () => {
    API.get("/tenant/webhooks").then(({ data }) => setWebhooks(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchWebhooks();
    API.get("/tenant/webhook-events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.url || form.events.length === 0) return toast.error("URL and at least one event required");
    setCreating(true);
    try {
      await API.post("/tenant/webhooks", form);
      toast.success("Webhook created");
      setShowCreate(false);
      setForm({ url: "", name: "", events: [] });
      fetchWebhooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create webhook");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/tenant/webhooks/${id}`);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleToggle = async (id, active) => {
    try {
      await API.put(`/tenant/webhooks/${id}`, { active: !active });
      fetchWebhooks();
    } catch {
      toast.error("Failed to update");
    }
  };

  const viewLogs = async (id) => {
    setLogsWebhookId(id);
    try {
      const { data } = await API.get(`/tenant/webhooks/${id}/logs`);
      setLogs(data);
    } catch {
      setLogs([]);
    }
  };

  const toggleEvent = (eventId) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(eventId) ? f.events.filter(e => e !== eventId) : [...f.events, eventId],
    }));
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading webhooks...</div>;

  return (
    <div data-testid="webhooks-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
            <Globe size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Webhooks</h1>
            <p className="text-xs text-muted-foreground">Send real-time event notifications to external services</p>
          </div>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-webhook-btn"><Plus size={14} className="mr-1" /> New Webhook</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="create-webhook-dialog">
            <DialogHeader><DialogTitle>Create Webhook</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Name (optional)</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="My Integration" className="mt-1" data-testid="webhook-name-input" />
              </div>
              <div>
                <Label>Endpoint URL</Label>
                <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://your-service.com/webhook" className="mt-1" required data-testid="webhook-url-input" />
              </div>
              <div>
                <Label className="mb-2 block">Events ({form.events.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border border-border rounded-md p-2 space-y-1" data-testid="webhook-events-list">
                  {events.map(ev => (
                    <label key={ev.event} className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-muted cursor-pointer text-xs">
                      <input type="checkbox" checked={form.events.includes(ev.event)} onChange={() => toggleEvent(ev.event)} className="rounded" />
                      <span>{ev.label}</span>
                      <span className="text-[9px] text-muted-foreground ml-auto font-mono">{ev.event}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={creating} data-testid="webhook-submit-btn">
                {creating ? "Creating..." : "Create Webhook"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <Globe size={36} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No webhooks configured yet</p>
          <p className="text-xs text-muted-foreground mt-1">Create a webhook to receive real-time event notifications</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="webhooks-list">
          {webhooks.map(wh => (
            <div key={wh.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`webhook-${wh.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold font-[Chivo] truncate">{wh.name}</h3>
                    <Badge className={`text-[8px] ${wh.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                      {wh.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground font-mono truncate mb-2">{wh.url}</p>
                  <div className="flex flex-wrap gap-1">
                    {wh.events.slice(0, 4).map(e => (
                      <span key={e} className="text-[8px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{e}</span>
                    ))}
                    {wh.events.length > 4 && <span className="text-[8px] text-muted-foreground">+{wh.events.length - 4} more</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Switch checked={wh.active} onCheckedChange={() => handleToggle(wh.id, wh.active)} data-testid={`toggle-webhook-${wh.id}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Send test" onClick={async () => {
                    try {
                      await API.post(`/tenant/webhooks/${wh.id}/test`);
                      toast.success("Test webhook sent");
                    } catch { toast.error("Test failed"); }
                  }} data-testid={`test-webhook-${wh.id}`}>
                    <Lightning size={12} className="text-amber-500" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => viewLogs(wh.id)} data-testid={`logs-webhook-${wh.id}`}>
                    <Eye size={12} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(wh.id)} data-testid={`delete-webhook-${wh.id}`}>
                    <Trash size={12} className="text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Logs Dialog */}
      <Dialog open={logs !== null} onOpenChange={open => !open && setLogs(null)}>
        <DialogContent className="max-w-lg" data-testid="webhook-logs-dialog">
          <DialogHeader><DialogTitle>Delivery Logs</DialogTitle></DialogHeader>
          {logs?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No deliveries yet</p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2">
              {logs?.map((log, i) => (
                <div key={i} className="border border-border rounded-md p-2.5 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      {log.success ? <CheckCircle size={12} className="text-emerald-500" weight="fill" /> : <XCircle size={12} className="text-red-500" weight="fill" />}
                      <span className="font-semibold">{log.event}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span>Status: {log.status_code || "N/A"}</span>
                    <span>Attempts: {log.attempts}</span>
                    {log.error && <span className="text-red-500">Error: {log.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
